import { useEffect, useState } from "react";
import { Badge } from "@freightsimple/ui-core/Display/Badge";
import { Banner } from "@freightsimple/ui-core/Display/Banner";
import { Button } from "@freightsimple/ui-core/Controls/Button";
import { Dialog } from "@freightsimple/ui-core/Dialog/Dialog";
import { Grid } from "@freightsimple/ui-core/PageLayout/Grid";
import { KeyValueList } from "@freightsimple/ui-core/Display/KeyValueList";
import { Panel } from "@freightsimple/ui-core/PageLayout/Panel";
import { Row } from "@freightsimple/ui-core/ComponentLayout/Row";
import { Stack } from "@freightsimple/ui-core/ComponentLayout/Stack";
import { T } from "@freightsimple/ui-core/Typography/T";
import { Table, type Column } from "@freightsimple/ui-core/Table/Table";
import { TextCell } from "@freightsimple/ui-core/Cells/TextCell";
import { type DialogBaseProps } from "@freightsimple/ui-core/Dialog/DialogBaseProps";
import { toast } from "sonner";
import { OpportunityAgreementStatus } from "@freightsimple/generated-carriers-openapi-client/models/OpportunityAgreementStatus";
import { type OpportunityDetail } from "@freightsimple/generated-carriers-openapi-client/models/OpportunityDetail";
import { type OpportunityLane } from "@freightsimple/generated-carriers-openapi-client/models/OpportunityLane";
import { type OpportunityBreakdown } from "@freightsimple/generated-carriers-openapi-client/models/OpportunityBreakdown";
import { type OpportunitySummary } from "@freightsimple/generated-carriers-openapi-client/models/OpportunitySummary";
import { carriersApi } from "@/App/Auth/carrierApi";
import { CarrierLoadFailed } from "@/App/CarrierLoadFailed";
import { formatMoney, formatPercent } from "@/App/Views/carrierFormatting";
import { describeCoverage } from "@/App/Views/opportunityFormatting";
import { opportunityHref } from "@/App/carrierRoutes";

interface OpportunityDialogProps extends DialogBaseProps {
  summary: OpportunitySummary;
  onRegistered: () => void;
}

/**
 * The high-level opportunity package: enough for a carrier to decide whether
 * this customer is worth pricing, without an email exchange.
 *
 * Of 22 recorded declines, 16 were coverage-fit or volume-threshold — every one
 * of them knowable before we made contact. That is what this dialog exists to
 * answer, which is why coverage, volume and the lane list lead.
 */
export function OpportunityDialog({
  summary,
  onRegistered,
  open,
  onOpenChange,
}: OpportunityDialogProps) {
  const [detail, setDetail] = useState<OpportunityDetail | undefined>(
    undefined,
  );
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const [registering, setRegistering] = useState(false);

  useEffect(() => {
    if (open !== true) return;
    let cancelled = false;
    setFailed(false);
    carriersApi()
      .getCarrierOpportunity({ companyId: summary.companyId })
      .then((data) => {
        if (!cancelled) setDetail(data);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [open, attempt, summary.companyId]);

  const interested =
    summary.agreementStatus !== OpportunityAgreementStatus.None;

  async function markInterested() {
    setRegistering(true);
    try {
      const result = await carriersApi().markCarrierOpportunityInterested({
        companyId: summary.companyId,
      });
      toast.success(
        result.alreadyRegistered
          ? "You're already registered for this opportunity"
          : `Thanks — we'll be in touch about ${summary.companyName}`,
      );
      onRegistered();
      onOpenChange?.(false);
    } catch {
      toast.error("Couldn't register your interest. Please try again.");
    } finally {
      setRegistering(false);
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={summary.companyName}
      description={summary.narrative}
      width="wide"
      footer={
        interested ? (
          <Button href={opportunityHref(summary.companyId)} primary>
            Open the full package
          </Button>
        ) : (
          <Button
            primary
            onClick={markInterested}
            loading={registering}
            data-testid="mark-interested-button"
          >
            I'm interested
          </Button>
        )
      }
    >
      <Stack gap={4}>
        {interested && (
          <Banner variant="success" title={summary.agreementStatusLabel}>
            You've registered interest in this customer. We'll be in touch about
            pricing.
          </Banner>
        )}
        <Headline_ summary={summary} detail={detail} />
        {failed && (
          <CarrierLoadFailed
            what="the detail for this opportunity"
            onRetry={() => setAttempt(attempt + 1)}
          />
        )}
        {detail && (
          <>
            <Grid columns={2} gap="lg">
              <PlacePanel_
                title="Where it ships from"
                description="Pickup locations over the data window."
                places={detail.pickupLocations}
              />
              <LanePanel_ lanes={detail.lanes} />
            </Grid>
            <FreightPanel_ detail={detail} />
          </>
        )}
      </Stack>
    </Dialog>
  );
}

function Headline_({
  summary,
  detail,
}: {
  summary: OpportunitySummary;
  detail: OpportunityDetail | undefined;
}) {
  const items = [
    {
      label: "Shipments",
      value: `${summary.shipmentsPerYear.toLocaleString("en-CA")} a year`,
    },
    {
      label: "Customer's freight spend",
      value:
        summary.estimatedAnnualFreightSpendCad === undefined
          ? "Not stated"
          : `${formatMoney(summary.estimatedAnnualFreightSpendCad)} a year`,
    },
    {
      label: "Your service area",
      value: describeCoverage(summary.coveragePercentage),
    },
    // "Recent shipments" is what carriers came back about repeatedly. Real
    // dates and a real count end that question.
    {
      label: "Data window",
      value:
        detail === undefined
          ? "—"
          : `${detail.dataWindow.fromDate} – ${detail.dataWindow.toDate} (${detail.dataWindow.shipmentCount.toLocaleString("en-CA")} shipments)`,
    },
  ];

  return (
    <Panel title="The opportunity">
      <Stack gap={3}>
        <KeyValueList items={items} />
        <Standing_ summary={summary} />
      </Stack>
    </Panel>
  );
}

/**
 * "You already see this freight and win 5% of it" is the most motivating line
 * we can put in front of a carrier, so it gets said explicitly rather than left
 * for them to infer.
 */
function Standing_({ summary }: { summary: OpportunitySummary }) {
  if (!summary.alreadyQuoting) {
    return (
      <T size="sm" color="secondary">
        You don't currently quote this customer's freight.
      </T>
    );
  }
  return (
    <Row gap={2} wrap>
      <Badge
        label={`You quote ${summary.quotedShipments?.toLocaleString("en-CA") ?? "0"} of their shipments`}
        variant="info"
      />
      {summary.winRate !== undefined && (
        <Badge
          label={`You win ${formatPercent(summary.winRate)} of them`}
          variant={summary.winRate > 0 ? "success" : "warning"}
        />
      )}
      {summary.averageRank !== undefined && (
        <Badge
          label={`Average price rank ${summary.averageRank.toFixed(1)}`}
          variant="neutral"
        />
      )}
    </Row>
  );
}

function PlacePanel_({
  title,
  description,
  places,
}: {
  title: string;
  description: string;
  places: OpportunityBreakdown[];
}) {
  const columns: Column<OpportunityBreakdown>[] = [
    {
      key: "label",
      header: "Location",
      render: (place) => <TextCell value={place.label} />,
    },
    {
      key: "count",
      header: "Shipments",
      align: "right",
      render: (place) => (
        <TextCell value={place.shipmentCount.toLocaleString("en-CA")} />
      ),
    },
    {
      key: "share",
      header: "Share",
      align: "right",
      render: (place) => (
        <TextCell value={formatPercent(place.sharePercentage)} color="muted" />
      ),
    },
  ];

  return (
    <Panel title={title} description={description} fullBleed>
      <Table
        columns={columns}
        data={places}
        rowKey={(place) => place.label}
        emptyMessage="No pickup locations recorded."
      />
    </Panel>
  );
}

function LanePanel_({ lanes }: { lanes: OpportunityLane[] }) {
  const columns: Column<OpportunityLane>[] = [
    {
      key: "lane",
      header: "Lane",
      render: (lane) => (
        <TextCell value={`${lane.pickup} → ${lane.delivery}`} />
      ),
    },
    {
      key: "count",
      header: "Shipments",
      align: "right",
      render: (lane) => (
        <TextCell value={lane.shipmentCount.toLocaleString("en-CA")} />
      ),
    },
    {
      key: "share",
      header: "Share",
      align: "right",
      render: (lane) => (
        <TextCell value={formatPercent(lane.sharePercentage)} color="muted" />
      ),
    },
  ];

  return (
    <Panel
      title="Busiest lanes"
      description="Lanes and origins are what carriers ask about most."
      fullBleed
    >
      <Table
        columns={columns}
        data={lanes}
        rowKey={(lane) => `${lane.pickup}|${lane.delivery}`}
        emptyMessage="No lanes recorded."
      />
    </Panel>
  );
}

function FreightPanel_({ detail }: { detail: OpportunityDetail }) {
  const profile = detail.freightProfile;
  const items = [
    {
      label: "Average weight",
      value:
        profile.averageWeightPounds === undefined
          ? "—"
          : `${Math.round(profile.averageWeightPounds).toLocaleString("en-CA")} lb`,
    },
    {
      label: "Average handling units",
      value: profile.averageHandlingUnits?.toFixed(1) ?? "—",
    },
    {
      label: "Average linear feet",
      value: profile.averageLinearFeet?.toFixed(1) ?? "—",
    },
  ];

  return (
    <Panel
      title="The freight"
      description="What this customer actually ships, from their booked shipments."
    >
      <Stack gap={3}>
        <KeyValueList items={items} />
        {profile.accessorials.length > 0 && (
          <Row gap={2} wrap>
            {profile.accessorials.map((accessorial) => (
              <Badge
                key={accessorial.label}
                label={`${accessorial.label} ${formatPercent(accessorial.sharePercentage)}`}
                variant="neutral"
              />
            ))}
          </Row>
        )}
      </Stack>
    </Panel>
  );
}
